import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Building2,
  Users,
  Award,
  TrendingUp,
  Shield,
  Heart,
  Sparkles,
  CheckCircle,
  FileText,
  Database,
  Zap,
  Wallet,
  Quote,
  UserCheck,
  Utensils,
  GraduationCap,
  X,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  Send,
  Star,
  ChevronDown,
  HelpCircle,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { notify } from "@/components/common/Notification";
import { useNavigate } from "react-router-dom";
import defaultInstitutionImage from "../../assets/hotels1.png";
import divyangImage from "../../assets/Visionary Leader.png";
// import heroImage from "../../assets/31c28e0970e650f34ae9b6d516d1bda8e8d5814f.png";
import { usePrefixedNavigate } from "@/projects/Jainboarding_Admin/hooks/usePrefixedNavigate";
import { hostels } from "./hostelData";
import HostelDetailPage from "./HostelDetailPage";
import DonorInquiryModal from "../DonorInquiryModal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "../ui/carousel";
import {
  publicInstitutionsAPI,
  faqAPI,
  testimonialsAPI,
  type InstitutionDetailItem,
  type InstitutionListItem,
  type FAQQuestion,
  type Testimonial,
} from "../../services/api";
import { Badge } from "../ui/badge";

interface EducationalYearItem {
  total_capacity: number;
  vacant_oocupied: number;
  educational_year: string;
}

interface DirectoryHostelItem {
  apiId: string;
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  type: string;
  capacity: number;
  teachingStaff?: number;
  nonTeachingStaff?: number;
  classrooms?: number;
  trusteeCount?: number;
  departments: string[];
  levels: string[];
  facilities: string[];
  scholarship: boolean;
  sicRating: number;
  image: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  images?: string[];
  selectedFacilities?: string[];
  selected_facilities?: string[];
  fees: string;
  trust: string;
  safetyRating: number;
  hygieneRating: number;
  academicSupport: number;
  culturalAlignment: number;
  governance: number;
  is_highlighted?: boolean;
  educational_year?: EducationalYearItem[];
  selected_levels?: string[];
  boarding_gender?: string;
  campus_area?: string;
  trustee_contact?: string;
  trustee_email?: string;
  trust_registration?: string;
  other_trustees?: string;
  trustee_designation?: string;
  video?: string;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => {
      if (typeof item === "string") {
        return item;
      }
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        const label = record.label ?? record.name ?? record.value;
        return typeof label === "string" ? label : "";
      }
      return "";
    })
    .filter(Boolean);
};

const parseEducationalYear = (value: unknown): EducationalYearItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item: unknown) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const row = item as Record<string, unknown>;
      const total_capacity = Number(row.total_capacity);
      const vacant_oocupied = Number(row.vacant_oocupied);
      const educational_year =
        typeof row.educational_year === "string"
          ? row.educational_year
          : String(row.educational_year ?? "");
      if (!educational_year) {
        return null;
      }
      return {
        total_capacity: Number.isFinite(total_capacity) ? total_capacity : 0,
        vacant_oocupied: Number.isFinite(vacant_oocupied) ? vacant_oocupied : 0,
        educational_year,
      };
    })
    .filter((x): x is EducationalYearItem => x !== null);
};

const isVideoUrl = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  return (
    /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url) ||
    url.includes("vimeo.com") ||
    url.includes("youtube.com") ||
    url.includes("youtu.be")
  );
};

const getInstitutionImage = (institution: InstitutionListItem): string => {
  if (typeof institution.banner === "string" && institution.banner.trim()) {
    return institution.banner;
  }

  const images = institution.images;
  if (Array.isArray(images) && typeof images[0] === "string" && images[0]) {
    return images[0];
  }

  return defaultInstitutionImage;
};

const getInstitutionImages = (institution: InstitutionListItem): string[] => {
  const images = institution.images;
  if (Array.isArray(images)) {
    return images.filter(
      image => typeof image === "string" && image.trim().length > 0
    ) as string[];
  }
  return [];
};

const mapInstitutionToDirectoryHostel = (
  institution: InstitutionListItem,
  index: number
): DirectoryHostelItem => {
  const idValue =
    typeof institution.id === "number"
      ? institution.id
      : Number(institution.id ?? index + 1);

  const safeId = Number.isFinite(idValue) ? idValue : index + 1;
  const capacityValue = toNumber(
    institution.capacity ?? institution.total_students,
    0
  );
  const ratingValue = toNumber(institution.sic_rating, 0);
  const departments = toStringArray(
    institution.selected_departments ?? institution.selectedDepartments
  );
  const facilities = toStringArray(
    institution.selected_facilities ?? institution.selectedFacilities
  );
  const levels = toStringArray(
    institution.selected_levels ?? institution.selectedLevels
  );

  return {
    apiId: String(institution.id ?? safeId),
    id: safeId,
    name: String(
      institution.name ?? institution.institution_name ?? "Institution"
    ),
    address: String(
      institution.address ??
        [institution.address_line1, institution.address_line2]
          .filter(Boolean)
          .join(", ") ??
        [institution.city ?? institution.district, institution.state]
          .filter(Boolean)
          .join(", ")
    ),
    city: String(institution.city ?? institution.district ?? "Unknown"),
    state: String(institution.state ?? "Unknown"),
    type: String(institution.type ?? institution.institution_type ?? "N/A"),
    capacity: capacityValue,
    teachingStaff: toNumber(institution.teaching_staff, 0),
    nonTeachingStaff: toNumber(institution.non_teaching_staff, 0),
    classrooms: toNumber(institution.classrooms, 0),
    trusteeCount: toNumber(institution.trustee_count, 0),
    departments,
    levels,
    facilities,
    scholarship: Boolean(
      institution.scholarship_available ?? institution.scholarship
    ),
    sicRating: ratingValue,
    image: getInstitutionImage(institution),
    contactPerson:
      typeof institution.contact_person === "string"
        ? institution.contact_person
        : undefined,
    email:
      typeof institution.email === "string" ? institution.email : undefined,
    phone:
      typeof institution.phone === "string" ? institution.phone : undefined,
    website:
      typeof institution.website === "string" ? institution.website : undefined,
    images: getInstitutionImages(institution),
    selectedFacilities: facilities,
    selected_facilities: facilities,
    fees: "₹8,000/month",
    trust: String(institution.trustee_name ?? "JBF Registered Trust"),
    safetyRating: 4.5,
    hygieneRating: 4.5,
    academicSupport: 4.5,
    culturalAlignment: 4.5,
    governance: 4.5,
    is_highlighted: Boolean(
      (institution as Record<string, unknown>).is_highlighted
    ),
    selected_levels: Array.isArray(
      (institution as Record<string, unknown>).selected_levels
    )
      ? (
          (institution as Record<string, unknown>).selected_levels as string[]
        ).filter((s): s is string => typeof s === "string")
      : undefined,
    educational_year: parseEducationalYear(
      (institution as Record<string, unknown>).educational_year
    ),
    boarding_gender:
      typeof (institution as Record<string, unknown>).boarding_gender ===
      "string"
        ? ((institution as Record<string, unknown>).boarding_gender as string)
        : undefined,
    campus_area:
      typeof (institution as Record<string, unknown>).campus_area === "string"
        ? ((institution as Record<string, unknown>).campus_area as string)
        : undefined,
    trustee_contact:
      typeof (institution as Record<string, unknown>).trustee_contact ===
      "string"
        ? ((institution as Record<string, unknown>).trustee_contact as string)
        : undefined,
    trustee_email:
      typeof (institution as Record<string, unknown>).trustee_email === "string"
        ? ((institution as Record<string, unknown>).trustee_email as string)
        : undefined,
    trust_registration:
      typeof (institution as Record<string, unknown>).trust_registration ===
      "string"
        ? ((institution as Record<string, unknown>)
            .trust_registration as string)
        : undefined,
    other_trustees:
      typeof (institution as Record<string, unknown>).other_trustees ===
      "string"
        ? ((institution as Record<string, unknown>).other_trustees as string)
        : undefined,
    trustee_designation:
      typeof (institution as Record<string, unknown>).trustee_designation ===
      "string"
        ? ((institution as Record<string, unknown>)
            .trustee_designation as string)
        : undefined,
    video:
      typeof (institution as Record<string, unknown>).video === "string"
        ? ((institution as Record<string, unknown>).video as string)
        : typeof (institution as Record<string, unknown>).video_link ===
            "string"
          ? ((institution as Record<string, unknown>).video_link as string)
          : isVideoUrl(getInstitutionImage(institution))
            ? getInstitutionImage(institution)
            : undefined,
  };
};

const mapInstitutionDetailToHostel = (
  institution: InstitutionDetailItem,
  fallback: DirectoryHostelItem
): DirectoryHostelItem => {
  const mapped = mapInstitutionToDirectoryHostel(institution, fallback.id - 1);
  return {
    ...fallback,
    ...mapped,
    id: fallback.id,
    apiId: String(institution.id ?? fallback.apiId),
    fees: fallback.fees,
    trust:
      typeof institution.trustee_name === "string" && institution.trustee_name
        ? institution.trustee_name
        : fallback.trust,
  };
};

export default function HomePage() {
  const USER_BASE_PATH = "";
  const navigate = useNavigate();
  const prefixedNavigate = usePrefixedNavigate();

  const [searchQuery] = useState("");
  const [selectedCity] = useState("All");
  const [selectedType] = useState("All");
  const [selectedField] = useState("All");
  const [selectedEducationLevel] = useState("All");
  const [selectedFacility] = useState("All");
  const [selectedScholarship] = useState("All");
  const [_showFilters] = useState(false);
  const [showDetailPage, setShowDetailPage] = useState(false);
  const [selectedHostel, setSelectedHostel] =
    useState<DirectoryHostelItem | null>(null);
  const [compareMode] = useState(false);
  const [selectedForComparison] = useState<number[]>([]);
  const [directoryHostels, setDirectoryHostels] = useState<
    DirectoryHostelItem[]
  >(
    hostels.map<DirectoryHostelItem>(hostel => ({
      ...hostel,
      apiId: String(hostel.id),
      departments: [],
      levels: [],
      contactPerson: "",
      email: "",
      phone: "",
      website: "",
      state: hostel.state ?? "Unknown",
    }))
  );
  const [institutionsListLoaded, setInstitutionsListLoaded] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showDonorModal, setShowDonorModal] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  const [faqs, setFaqs] = useState<FAQQuestion[]>([]);
  const [faqCategories, setFaqCategories] = useState<string[]>(["General"]);
  const [selectedFaqCategory, setSelectedFaqCategory] =
    useState<string>("General");
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
  const [testimonialSnap, setTestimonialSnap] = useState(0);

  const filteredFaqItems = faqs.filter(
    item => item.category_title === selectedFaqCategory
  );

  /** Institutions with is_highlighted === true for the featured banner */
  const highlightedInstitutions = useMemo(
    () => directoryHostels.filter(h => h.is_highlighted === true),
    [directoryHostels]
  );

  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const institutions = await publicInstitutionsAPI.getInstitutionsList();
        if (institutions && institutions.length > 0) {
          setDirectoryHostels(
            institutions.map((institution, index) =>
              mapInstitutionToDirectoryHostel(institution, index)
            )
          );
        } else {
          setDirectoryHostels([]);
        }
      } catch (error) {
        console.error("Failed to load institutions list:", error);
        setDirectoryHostels([]);
      } finally {
        setInstitutionsListLoaded(true);
      }
    };

    const loadFAQs = async () => {
      setLoadingFaqs(true);
      try {
        const data = await faqAPI.getFAQList();
        setFaqs(data);
        const categories = Array.from(
          new Set(data.map(q => q.category_title))
        ).sort();
        if (categories.length > 0) {
          setFaqCategories(categories);
          setSelectedFaqCategory(categories[0]);
        }
      } catch (error) {
        console.error("Failed to load FAQs:", error);
      } finally {
        setLoadingFaqs(false);
      }
    };

    const loadTestimonials = async () => {
      setLoadingTestimonials(true);
      try {
        const data = await testimonialsAPI.getTestimonialList();
        setTestimonials(data);
      } catch (error) {
        console.error("Failed to load testimonials:", error);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    void loadInstitutions();
    void loadFAQs();
    void loadTestimonials();
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    setTestimonialSnap(emblaApi.selectedScrollSnap());
    const onSelect = () => setTestimonialSnap(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      void emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    const interval = setInterval(() => emblaApi.scrollNext(), 4000);
    return () => clearInterval(interval);
  }, [emblaApi]);

  const [hostelFormData, setHostelFormData] = useState({
    hostelName: "",
    trustName: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    contactPerson: "",
    phone: "",
    email: "",
    capacity: "",
    currentOccupancy: "",
  });
  const handleProceedToForm = () => {
    setShowRegistrationModal(false);
    setShowRegistrationForm(true);
  };

  const handleHostelFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setHostelFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredHostels = directoryHostels.filter(hostel => {
    const matchesSearch =
      hostel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hostel.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = selectedCity === "All" || hostel.city === selectedCity;
    const matchesType = selectedType === "All" || hostel.type === selectedType;
    const matchesField =
      selectedField === "All" || hostel.departments.includes(selectedField);
    const matchesEducationLevel =
      selectedEducationLevel === "All" ||
      hostel.levels.includes(selectedEducationLevel);
    const matchesFacility =
      selectedFacility === "All" ||
      hostel.facilities.includes(selectedFacility);
    const matchesScholarship =
      selectedScholarship === "All" ||
      (selectedScholarship === "Available"
        ? hostel.scholarship
        : !hostel.scholarship);
    return (
      matchesSearch &&
      matchesCity &&
      matchesType &&
      matchesField &&
      matchesEducationLevel &&
      matchesFacility &&
      matchesScholarship
    );
  });
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      // Here you would typically send the data to your API
      console.log("Hostel Registration Data:", hostelFormData);

      notify.success(
        "Registration Submitted Successfully",
        "We'll contact you soon. Thank you for registering!"
      );
      setShowRegistrationForm(false);
      // Reset form after successful submission
      setHostelFormData({
        hostelName: "",
        trustName: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        contactPerson: "",
        phone: "",
        email: "",
        capacity: "",
        currentOccupancy: "",
      });
    } catch (error: any) {
      console.error("Form submission error:", error);
      notify.error(
        "Registration Failed",
        error?.message ?? "Unable to submit registration. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDetails = async (hostel: DirectoryHostelItem) => {
    try {
      setIsDetailLoading(true);
      const detail = await publicInstitutionsAPI.getInstitutionDetail(
        hostel.apiId
      );
      setSelectedHostel(mapInstitutionDetailToHostel(detail, hostel));
      setShowDetailPage(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to load institution detail:", error);
      notify.error(
        "Unable to load details",
        "Please try again after some time."
      );
    } finally {
      setIsDetailLoading(false);
    }
  };

  if (showDetailPage && selectedHostel) {
    return (
      <HostelDetailPage
        hostel={selectedHostel}
        onBack={() => {
          setShowDetailPage(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Image - Matching Figma Design */}
      <section className="relative flex min-h-[90vh] items-center overflow-hidden bg-[#7A1E1E]">
        <style>{`
          @keyframes hero-badge-zoom {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.08); }
          }
          @keyframes fade-in-up {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .hero-badge-zoom {
            animation: hero-badge-zoom 2s ease-in-out infinite;
            transform-origin: center;
          }
          .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
          }
          .reveal-on-scroll {
            opacity: 0;
            transform: translateY(30px);
            transition: all 0.8s ease-out;
          }
          .reveal-on-scroll.revealed {
            opacity: 1;
            transform: translateY(0);
          }
        `}</style>
        {/* Laptop/desktop only: Secure Your Admission badge - top right */}
        <div className="absolute right-6 top-8 z-10 hidden lg:block">
          <span className="hero-badge-zoom inline-block">
            <Badge
              variant="outline"
              className="cursor-pointer gap-1.5 bg-white px-3 py-1.5 sm:text-base"
              onClick={() => prefixedNavigate("/student/register")}
            >
              <ShieldCheck className="h-4.5 w-4.5 shrink-0 text-green-600" />
              <span className="text-[#7A1E1E]">Secure Your Admission</span>
            </Badge>
          </span>
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-2 lg:grid-cols-2 lg:gap-0">
            {/* 1. JBF text - mobile: first; lg: left column (col 1) */}
            <div className="order-1 text-white lg:order-1 lg:col-start-1 lg:row-start-1">
              {/* Mobile: Secure Your Admission in content flow (no overlap). Laptop: only top-right absolute badge. */}
              <div className="mb-4 mt-3 flex flex-col gap-3 sm:mb-6 sm:mt-0 sm:flex-row sm:flex-wrap sm:gap-4">
                <span className="hero-badge-zoom inline lg:hidden">
                  <Badge
                    variant="outline"
                    className="w-full justify-center gap-1.5 rounded-lg border-white/80 bg-white px-3 py-2 text-sm backdrop-blur-sm"
                    onClick={() => prefixedNavigate("/student/register")}
                  >
                    <ShieldCheck className="h-4 w-4 shrink-0 text-green-400" />
                    <span className="text-[#7A1E1E]">
                      Secure Your Admission
                    </span>
                  </Badge>
                </span>
                <div className="animate-fade-in-up inline-flex max-w-full items-center gap-2 rounded-full border border-dashed border-white px-3 py-2 text-xs font-semibold text-white opacity-0 sm:px-4 sm:text-sm">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  <span className="leading-tight">
                    Transforming Student Lives Across India
                  </span>
                </div>
              </div>
              <h1
                className="animate-fade-in-up mb-4 text-3xl font-bold leading-tight text-white opacity-0 sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
                style={{ animationDelay: "0.1s" }}
              >
                Jain Boarding Federation (JBF)
              </h1>
              <p
                className="animate-fade-in-up mb-2 text-base leading-relaxed text-white opacity-0 sm:mb-0 sm:text-lg"
                style={{ animationDelay: "0.2s" }}
              >
                Empowering Jain students with modern hostels, governance,
                transparency, and developmental excellence.
              </p>
            </div>

            {/* 2. Video + Image combination - mobile: second; lg: right column (col 2), span both rows */}
            <div className="relative order-2 mb-10 mt-0 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:block lg:self-stretch">
              <div className="flex items-center justify-center overflow-hidden rounded-2xl">
                {/* Video - left side, 30% width */}
                <video
                  src="https://s3.ap-south-1.amazonaws.com/jbf.universal/Agar+khana+ghar+jaisa+ho.mp4"
                  className="h-[200px] w-[40%] min-w-0 flex-shrink-0 rounded-2xl object-cover sm:h-[300px] lg:h-[450px] lg:min-h-[450px]"
                  autoPlay
                  loop
                  muted
                  playsInline
                  title=""
                  onError={e => {
                    console.error("Video failed to load:", e);
                  }}
                />
                {/* Image - right side, 70% width */}
                {/* <img
                  src={heroImage}
                  alt="JBF hostel"
                  className="h-[200px] w-[60%] min-w-0 flex-shrink-0 object-cover sm:h-[300px] lg:h-[450px] lg:min-h-[450px]"
                /> */}
              </div>
              {/* 100% Transparent Card */}
              <div
                className="animate-fade-in-up absolute -bottom-4 right-4 rounded-xl bg-white p-2 opacity-0 shadow-2xl sm:-bottom-10 sm:right-20 sm:w-auto sm:rounded-2xl sm:p-4"
                style={{ animationDelay: "0.6s" }}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#7A1E1E] sm:h-10 sm:w-10">
                    <CheckCircle className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                  </div>
                  <div>
                    <div className="text-base font-bold text-[#7A1E1E] sm:text-lg lg:text-xl">
                      100% Transparent
                    </div>
                    <div className="text-xs text-gray-600 sm:text-sm">
                      Governance Framework
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Four buttons - mobile: third (below image); lg: left column (col 1) below text */}
            <div className="order-3 flex flex-col gap-3 text-white lg:col-start-1 lg:row-start-2 lg:mt-0 lg:gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <button
                  onClick={() => {
                    const quickAccessSection =
                      document.getElementById("quick-access");
                    if (quickAccessSection) {
                      quickAccessSection.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                  className="group flex items-center justify-center gap-2 rounded-lg bg-[#D4A017] px-6 py-2.5 text-sm text-white transition-all hover:bg-[#B8900F] sm:px-8 sm:py-3 sm:text-base"
                >
                  Join the JBF Network
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 sm:h-5 sm:w-5" />
                </button>
                {/* <button
                  onClick={() =>
                    navigate(`${USER_BASE_PATH}/impact-transparency`)
                  }
                  className="flex items-center justify-center gap-2 rounded-lg border-2 border-white bg-transparent px-6 py-2.5 text-sm text-white transition-all hover:bg-white/10 sm:px-8 sm:py-3 sm:text-base"
                >
                  Explore Impact Transparency
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                </button> */}
                <button
                  onClick={() => setShowDonorModal(true)}
                  className="flex items-center justify-center gap-2 rounded-lg border-2 border-white bg-transparent px-6 py-2.5 text-sm text-white transition-all hover:bg-white/10 sm:px-8 sm:py-3 sm:text-base"
                >
                  Make a Donation
                  <Heart className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              <div className="mb-4 flex flex-col gap-3 sm:mb-0 sm:flex-row sm:gap-4">
                <button
                  onClick={() =>
                    prefixedNavigate(
                      "/jainboarding-admin/auth?portal=institution"
                    )
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-white bg-transparent px-6 py-2.5 text-sm text-white transition-all hover:bg-white/10 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
                >
                  Institution Registration
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => prefixedNavigate("/student/register")}
                  className="group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-white bg-white/10 px-6 py-2.5 text-sm font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.3)] ring-2 ring-white/20 transition-all hover:bg-white/20 hover:ring-white/40 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
                >
                  Student Registration
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured institutions banner: is_highlighted === true from API; fallback to static images when none */}
      <section
        className="relative z-10 bg-white px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10"
        ref={el => {
          if (el) {
            const observer = new IntersectionObserver(
              entries => {
                entries.forEach(entry => {
                  const items = el.querySelectorAll(".reveal-on-scroll");
                  if (entry.isIntersecting) {
                    items.forEach((item, index) => {
                      setTimeout(() => {
                        item.classList.add("revealed");
                      }, index * 100);
                    });
                  } else {
                    items.forEach(item => {
                      item.classList.remove("revealed");
                    });
                  }
                });
              },
              { threshold: 0.1 }
            );
            observer.observe(el);
          }
        }}
      >
        <div className="mx-auto max-w-[1400px] sm:-mt-28 lg:-mt-32">
          <div className="grid grid-cols-2 grid-rows-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:grid-rows-1 lg:gap-5">
            {highlightedInstitutions.length > 0
              ? highlightedInstitutions.slice(0, 4).map(inst => (
                  <button
                    key={inst.apiId}
                    type="button"
                    className="reveal-on-scroll group flex flex-col overflow-hidden rounded-2xl bg-white text-left shadow-lg ring-1 ring-black/5 transition-shadow hover:shadow-xl sm:rounded-2xl"
                    onClick={e => {
                      e.preventDefault();
                      void handleViewDetails(inst);
                    }}
                  >
                    <img
                      src={inst.image || defaultInstitutionImage}
                      alt={inst.name}
                      className="h-28 w-full flex-shrink-0 object-cover sm:h-36 lg:h-44"
                    />
                    <div className="flex min-h-[52px] flex-shrink-0 flex-col justify-center px-2 py-1.5 sm:min-h-[56px] sm:px-3 sm:py-2">
                      <p className="truncate text-xs font-medium text-gray-800 group-hover:text-[#7A1E1E] sm:text-sm">
                        {inst.name || ""}
                      </p>
                      <p className="min-h-[1rem] truncate text-xs text-gray-500 sm:min-h-[1.25rem] sm:text-sm">
                        {[inst.city, inst.state]
                          .filter(
                            v =>
                              v !== null &&
                              v !== "" &&
                              String(v).toLowerCase() !== "unknown"
                          )
                          .join(", ") || "\u00A0"}
                      </p>
                    </div>
                  </button>
                ))
              : null}
          </div>
        </div>
      </section>

      {/* Message from Leadership - Divyang Bhai (no divider line) */}
      <section className="relative overflow-hidden bg-white py-8 sm:py-10 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-6 sm:gap-8 lg:grid-cols-2">
            {/* Left Side - Image */}
            <div className="relative order-2 flex w-full justify-center lg:order-1">
              <div className="relative w-max overflow-hidden rounded-xl border-2 border-[#D4A017] shadow-xl sm:rounded-2xl">
                <img
                  src={divyangImage}
                  alt="Divyang Bhai"
                  className="h-[240px] w-auto object-cover object-center sm:h-[360px] lg:h-[400px]"
                />
                <div className="absolute bottom-2 left-2 rounded-lg border-l-4 border-[#D4A017] bg-white/95 p-2 shadow-lg backdrop-blur-sm sm:bottom-5 sm:left-5 sm:p-3">
                  <h3 className="mb-0.5 text-xs text-[#7A1E1E] sm:text-lg">
                    Divyang Bhai
                  </h3>
                  <p className="text-[10px] text-gray-600 sm:text-sm">
                    Visionary Leader
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side - Message */}
            <div className="order-1 lg:order-2">
              <div className="mb-2 inline-block rounded-full border border-[#D4A017]/20 bg-[#FFF9F0] px-3 py-1.5 text-xs text-[#7A1E1E] sm:mb-3 sm:px-3.5 sm:py-1.5 sm:text-sm">
                Message from Leadership
              </div>

              <h2 className="mb-4 text-xl text-[#7A1E1E] sm:mb-6 sm:text-2xl md:text-3xl lg:text-4xl">
                A Revolution in Progress
              </h2>

              {/* Quote Icon */}
              <Quote className="mb-3 h-7 w-7 text-[#D4A017] sm:mb-4 sm:h-8 sm:w-8" />

              {/* Message Content */}
              <div className="relative">
                <div className="absolute bottom-0 left-0 top-0 w-1 rounded-full bg-gradient-to-b from-[#7A1E1E] via-[#D4A017] to-[#7A1E1E]" />
                <div className="space-y-3 pl-5 sm:space-y-4 sm:pl-6">
                  <p className="text-sm italic leading-relaxed text-gray-700 sm:text-base lg:text-lg">
                    JBF is not just a platform, it is a revolution in how we
                    nurture, guide and uplift our Jain youth.
                  </p>

                  <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                    Our SIRC-based model ensures accountability, transparency,
                    and results that matter.
                  </p>

                  <div className="rounded-lg border-l-4 border-[#D4A017] bg-gradient-to-r from-[#7A1E1E]/5 to-[#D4A017]/5 p-3 sm:p-4">
                    <p className="text-sm text-gray-700 sm:text-base">
                      Join us in this noble journey.
                    </p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="mt-4 flex flex-col gap-2 sm:mt-6 sm:flex-row sm:gap-3">
                <button
                  className="group flex transform items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#7A1E1E] to-[#8B2E2E] px-5 py-2 text-sm text-white transition-all hover:-translate-y-0.5 hover:shadow-lg sm:rounded-lg sm:px-6 sm:py-2.5"
                  onClick={() => navigate("/committee")}
                >
                  Learn More About Our Mission
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hostel Directory */}
      <section className="relative overflow-hidden bg-white bg-gradient-to-t from-white to-[#fff5e6ec] py-20 lg:py-20">
        <div className="absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />

        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-semibold text-[#7A1E1E] lg:text-5xl">
              Institution Directory
            </h2>
            <div className="mx-auto mb-6 h-1 w-32 bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              Explore the list of hostels registered with JBF.
            </p>
          </div>
          {/* <div className="mb-8">
            <h2 className="mb-2 text-3xl text-[#7A1E1E]">
              National Hostel Directory
            </h2>
            <p className="text-gray-700">
              Found {filteredHostels.length} hostels
            </p>
          </div> */}

          {institutionsListLoaded && directoryHostels.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-lg text-gray-700">
                No institutions available at the moment
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredHostels.slice(0, 3).map(hostel => (
                  <div
                    key={hostel.id}
                    className={`overflow-hidden rounded-xl bg-white shadow-lg transition-all hover:shadow-2xl ${
                      selectedForComparison.includes(hostel.id)
                        ? "ring-4 ring-[#D4A017]"
                        : ""
                    }`}
                  >
                    <div className="relative h-48">
                      {hostel.video ? (
                        isVideoUrl(hostel.video) &&
                        (hostel.video.includes("youtube.com") ||
                          hostel.video.includes("youtu.be") ||
                          hostel.video.includes("vimeo.com")) ? (
                          <iframe
                            title={hostel.name}
                            src={
                              hostel.video.includes("youtube.com") ||
                              hostel.video.includes("youtu.be")
                                ? `https://www.youtube.com/embed/${
                                    hostel.video.includes("v")
                                      ? hostel.video
                                          .split("v=")[1]
                                          ?.split("&")[0]
                                      : hostel.video.split("/").pop()
                                  }`
                                : `https://player.vimeo.com/video/${hostel.video
                                    .split("/")
                                    .pop()}`
                            }
                            className="pointer-events-none h-full w-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            onMouseOver={e => {
                              (e.target as HTMLIFrameElement).src +=
                                "&autoplay=1&mute=1";
                            }}
                          />
                        ) : (
                          <video
                            src={hostel.video}
                            className="h-full w-full object-cover"
                            controls={false}
                            autoPlay
                            muted
                            loop
                            playsInline
                          />
                        )
                      ) : (
                        <img
                          src={hostel.image}
                          alt={hostel.name}
                          className="h-full w-full object-cover"
                        />
                      )}
                      <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-[#D4A017] px-3 py-1 text-white">
                        <Star className="h-4 w-4 fill-current" />
                        <span className="text-sm">{hostel.sicRating}</span>
                      </div>
                      {compareMode && (
                        <button
                          // onClick={() => toggleComparison(hostel.id)}
                          className={`absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full ${
                            selectedForComparison.includes(hostel.id)
                              ? "bg-[#D4A017] text-white"
                              : "bg-white text-gray-700"
                          }`}
                        >
                          {selectedForComparison.includes(hostel.id) ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-400" />
                          )}
                        </button>
                      )}
                    </div>

                    <div className="p-6">
                      <h3 className="mb-2 text-xl text-[#7A1E1E]">
                        {hostel.name}
                      </h3>

                      <div className="mb-4 space-y-2">
                        <div className="flex items-start gap-2 text-sm text-gray-700">
                          <MapPin className="mt-1 h-4 w-4 flex-shrink-0 text-[#D4A017]" />
                          <span className="text-[16px]">{hostel.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Users className="h-4 w-4 text-[#D4A017]" />
                          <span className="text-[16px]">
                            Capacity: {hostel.capacity} | {hostel.type}
                          </span>
                        </div>
                      </div>

                      {/* <div className="mb-4 min-h-[48px]">
                        <div className="flex items-center gap-2 rounded bg-green-50 px-3 py-2 text-sm text-green-700">
                          <Award className="h-4 w-4" />
                          <span className="text-[16px]">
                            Scholarship Available
                          </span>
                        </div>
                      </div> */}

                      <button
                        onClick={() => {
                          void handleViewDetails(hostel);
                        }}
                        disabled={isDetailLoading}
                        className="w-full rounded-lg bg-[#7A1E1E] py-3 text-white transition-all hover:bg-[#5A1515] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isDetailLoading ? "Loading..." : "View Details"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 text-center">
                <button
                  onClick={() => navigate("/hostel-directory")}
                  className="rounded-lg bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-8 py-3 text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  View More
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* JBF + MAA Collaboration - Visual Section */}
      {/* <section className="relative overflow-hidden bg-white py-20 lg:py-32">
        <div className="absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-[#D4A017] blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-[#7A1E1E] blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <div className="mb-4 inline-block rounded-full border border-[#D4A017]/30 bg-[#FFF9F0] px-4 py-2 text-sm text-[#7A1E1E] backdrop-blur-sm">
              Strategic Partnership
            </div>
            <h2 className="mb-6 text-4xl text-[#7A1E1E] lg:text-5xl">
              Lead System Architect & Consultancy
            </h2>
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              Through a structured partnership with consultants, JBF adopts the
              globally pioneering S-ARDA doctrine.
            </p>
          </div>

          <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Shield className="h-8 w-8" />,
                title: "Governance Reconstruction",
                desc: "Complete structural reform",
              },
              {
                icon: <FileText className="h-8 w-8" />,
                title: "SOP-driven Admin",
                desc: "Standard procedures",
              },
              {
                icon: <Heart className="h-8 w-8" />,
                title: "Safety & Welfare",
                desc: "Student-first approach",
              },
              {
                icon: <TrendingUp className="h-8 w-8" />,
                title: "SROI Measurement",
                desc: "Impact tracking",
              },
              {
                icon: <Award className="h-8 w-8" />,
                title: "SIC Credits",
                desc: "Recognition system",
              },
              {
                icon: <Database className="h-8 w-8" />,
                title: "HDT Tokens",
                desc: "Development tracking",
              },
              {
                icon: <CheckCircle className="h-8 w-8" />,
                title: "Ledger Verified",
                desc: "Full transparency",
              },
              {
                icon: <Sparkles className="h-8 w-8" />,
                title: "Values Integration",
                desc: "Jain principles",
              },
            ].map(item => (
              <div
                key={item.title}
                className="group rounded-2xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6 transition-all hover:border-[#D4A017]/40 hover:shadow-lg"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4A017] to-[#B8900F] text-white transition-transform group-hover:scale-110">
                  {item.icon}
                </div>
                <h3 className="mb-2 text-lg text-[#7A1E1E]">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-[#D4A017]/30 bg-gradient-to-r from-[#D4A017]/10 to-transparent p-8 text-center">
            <Sparkles className="mx-auto mb-4 h-12 w-12 text-[#D4A017]" />
            <p className="text-xl text-[#7A1E1E]">
              No compromise on Jain identity, principles, culture or values.
            </p>
          </div>
        </div>
      </section> */}

      {/* Quick Access - scroll-margin so section is not cropped when scrolled from hero */}
      <section
        id="quick-access"
        className="scroll-mt-20 bg-white py-12 sm:scroll-mt-24 sm:py-16 lg:py-20 xl:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center sm:mb-12 lg:mb-16">
            <h2 className="mb-3 text-2xl font-semibold text-[#7A1E1E] sm:mb-4 sm:text-3xl md:text-4xl lg:text-5xl">
              Quick Access
            </h2>
            <p className="text-base text-gray-600 sm:text-lg lg:text-xl">
              Everything you need, just a click away
            </p>
          </div>

          <div className="mx-auto max-w-6xl">
            {/* First Row - 4 buttons */}
            <div className="mb-4 grid grid-cols-1 gap-4 sm:mb-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
              {[
                {
                  label: "Register a Hostel",
                  icon: <Building2 className="h-6 w-6" />,
                  color: "from-[#7A1E1E] to-[#8B2E2E]",
                  onClick: () => setShowRegistrationModal(true),
                },
                {
                  label: "Donor / CSR Portal",
                  icon: <Heart className="h-6 w-6" />,
                  color: "from-[#D4A017] to-[#B8900F]",
                  onClick: () => setShowDonorModal(true),
                },
                {
                  label: "Student Search Portal",
                  icon: <Users className="h-6 w-6" />,
                  color: "from-[#7A1E1E] to-[#8B2E2E]",
                },
                {
                  label: "JBF Central Directory",
                  icon: <Database className="h-6 w-6" />,
                  color: "from-[#D4A017] to-[#B8900F]",
                },
              ].map(link => (
                <button
                  key={link.label}
                  onClick={() => {
                    if (
                      link.label === "Student Search Portal" ||
                      link.label === "JBF Central Directory"
                    ) {
                      navigate(`${USER_BASE_PATH}/impact-transparency`);
                    } else if (link.onClick) {
                      link.onClick();
                    }
                  }}
                  className="group relative w-full overflow-hidden rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-4 transition-all hover:border-[#D4A017] hover:shadow-xl sm:rounded-2xl sm:p-6"
                >
                  <div className="flex flex-col items-center text-center">
                    <div
                      className={`h-12 w-12 bg-gradient-to-br ${link.color} mb-3 flex items-center justify-center rounded-lg text-white transition-transform group-hover:scale-110 sm:mb-4 sm:h-14 sm:w-14 sm:rounded-xl`}
                    >
                      {link.icon}
                    </div>
                    <span className="text-sm text-gray-800 transition-colors group-hover:text-[#7A1E1E] sm:text-base">
                      {link.label}
                    </span>
                  </div>
                  <ArrowRight className="absolute bottom-3 right-3 h-4 w-4 translate-x-2 transform text-[#D4A017] opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100 sm:bottom-4 sm:right-4 sm:h-5 sm:w-5" />
                </button>
              ))}
            </div>

            {/* Second Row - 3 buttons centered */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:justify-center">
              {[
                {
                  label: "SaaS Integration",
                  icon: <Zap className="h-6 w-6" />,
                  color: "from-[#7A1E1E] to-[#8B2E2E]",
                },
                {
                  label: "Governance Constitution",
                  icon: <FileText className="h-6 w-6" />,
                  color: "from-[#D4A017] to-[#B8900F]",
                },
                {
                  label: "Hostel Development Fund",
                  icon: <Wallet className="h-6 w-6" />,
                  color: "from-[#7A1E1E] to-[#8B2E2E]",
                },
              ].map(link => (
                <button
                  key={link.label}
                  onClick={() =>
                    navigate(`${USER_BASE_PATH}/impact-transparency`)
                  }
                  className="group relative w-full overflow-hidden rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-4 transition-all hover:border-[#D4A017] hover:shadow-xl sm:rounded-2xl sm:p-6 lg:w-full"
                >
                  <div className="flex flex-col items-center text-center">
                    <div
                      className={`h-12 w-12 bg-gradient-to-br ${link.color} mb-3 flex items-center justify-center rounded-lg text-white transition-transform group-hover:scale-110 sm:mb-4 sm:h-14 sm:w-14 sm:rounded-xl`}
                    >
                      {link.icon}
                    </div>
                    <span className="text-sm text-gray-800 transition-colors group-hover:text-[#7A1E1E] sm:text-base">
                      {link.label}
                    </span>
                  </div>
                  <ArrowRight className="absolute bottom-3 right-3 h-4 w-4 translate-x-2 transform text-[#D4A017] opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100 sm:bottom-4 sm:right-4 sm:h-5 sm:w-5" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Types of Contributions */}
      <section className="bg-white py-20 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-semibold text-[#7A1E1E] lg:text-5xl">
              Opportunities for the Donors
            </h2>
            <div className="mx-auto mb-6 h-1 w-32 bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              Choose how you want to make an impact. Every contribution area is
              fully tracked and measured.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Infrastructure */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Building2 className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Infrastructure</h3>
                <p className="text-sm text-white/90">
                  Fund hostel construction, renovation, and modernization
                  projects
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Building upgrades</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Dormitory renovation</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Safety systems</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Modern facilities</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Student Sponsorship */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <UserCheck className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Student Sponsorship</h3>
                <p className="text-sm text-white/90">
                  Support individual students through their educational journey
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Full sponsorship</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Partial support</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Merit scholarships</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Need-based aid</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Nutrition & Health */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Utensils className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Nutrition & Health</h3>
                <p className="text-sm text-white/90">
                  Ensure quality food and healthcare for students
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Meal programs</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Health check-ups</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Medical facilities</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Sports equipment</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Academic/Coaching Programs */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <GraduationCap className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Academic/Coaching Programs</h3>
                <p className="text-sm text-white/90">
                  Provide educational support and skill development
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Tutoring sessions</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Exam preparation</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Skill workshops</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Career guidance</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Digital Transformation */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Database className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Digital Transformation</h3>
                <p className="text-sm text-white/90">
                  Enable technology and digital systems for hostels
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>S-ARDA platform</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Management software</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Digital libraries</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Online portals</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Multi-year Fellowships */}
            <div className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md">
              <div className="flex h-[220px] flex-col items-center justify-center bg-[#7A1E1E] p-8 text-center text-white">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Award className="h-10 w-10" />
                </div>
                <h3 className="mb-2 text-2xl">Multi-year Fellowships</h3>
                <p className="text-sm text-white/90">
                  Long-term commitment to student development
                </p>
              </div>
              <div className="flex-1 bg-[#FFF9F0] p-6">
                <p className="mb-3 text-sm text-gray-600">Examples:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Research grants</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Leadership programs</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Internship support</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1 text-[#D4A017]">●</span>
                    <span>Mentorship initiatives</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Testimonials Section - Carousel (Embla) */}
      <section
        id="testimonials"
        className="scroll-mt-20 bg-white py-16 sm:py-20 lg:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center sm:mb-12">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D4A017]/30 bg-[#FFF9F0] px-4 py-2 text-sm text-[#7A1E1E]">
              <Star className="h-4 w-4 fill-[#D4A017] text-[#D4A017]" />
              What people say
            </div>
            <h2 className="mb-3 text-2xl font-semibold text-[#7A1E1E] sm:mb-4 sm:text-3xl md:text-4xl lg:text-5xl">
              User Testimonials
            </h2>
            <div className="mx-auto mb-4 h-1 w-32 bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />
            <p className="mx-auto max-w-2xl text-base text-gray-600 sm:text-lg">
              Stories from students, parents, donors, and alumni who are part of
              the JBF community.
            </p>
          </div>

          <Carousel
            opts={{ loop: true, align: "start", dragFree: false }}
            setApi={setEmblaApi}
            slidesPerView={3}
            className="relative mx-auto w-full max-w-6xl"
          >
            <CarouselContent className="-ml-0">
              {loadingTestimonials ? (
                <div className="flex h-64 w-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-[#D4A017]" />
                </div>
              ) : testimonials.length === 0 ? (
                <div className="flex h-64 w-full items-center justify-center rounded-xl border border-dashed border-[#D4A017]/30 bg-white/50">
                  <p className="italic text-gray-500">
                    No testimonials yet. Be the first to share your experience!
                  </p>
                </div>
              ) : (
                testimonials.map(t => {
                  const getIcon = (cat: string) => {
                    switch (cat.toLowerCase()) {
                      case "student":
                        return GraduationCap;
                      case "parent":
                        return Users;
                      case "donor":
                        return Heart;
                      case "trustee / volunteer":
                        return Shield;
                      case "alumni":
                        return Award;
                      default:
                        return UserCheck;
                    }
                  };
                  const Icon = getIcon(t.category);
                  return (
                    <CarouselItem key={t.id} className="h-full">
                      <div className="flex h-full min-h-[340px] flex-col rounded-xl border border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-6 shadow-md sm:min-h-[360px] sm:p-8">
                        <div className="mb-5 flex items-center justify-between align-middle">
                          <Quote className="h-8 w-8 shrink-0 text-[#D4A017]/80 sm:h-9 sm:w-9" />
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 shrink-0 rounded-full bg-[#7A1E1E] p-1 text-white sm:h-6 sm:w-6" />
                            <p className="text-sm font-medium text-[#7A1E1E]">
                              {t.category}
                            </p>
                          </div>
                        </div>
                        <p className="min-h-0 flex-1 text-sm leading-relaxed text-gray-700 sm:text-base">
                          {t.description}
                        </p>
                        <div className="mt-4 shrink-0 border-t border-[#D4A017]/20 pt-4">
                          <p className="font-semibold text-[#7A1E1E]">
                            {t.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {t.designation}
                          </p>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })
              )}
            </CarouselContent>
            <CarouselPrevious
              className="left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border-[#D4A017]/30 bg-white p-2 text-[#7A1E1E] shadow-md transition-colors hover:border-[#D4A017] hover:bg-[#FFF9F0] md:-left-4"
              aria-label="Previous testimonial"
            />
            <CarouselNext
              className="right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border-[#D4A017]/30 bg-white p-2 text-[#7A1E1E] shadow-md transition-colors hover:border-[#D4A017] hover:bg-[#FFF9F0] md:-right-4"
              aria-label="Next testimonial"
            />
          </Carousel>

          {/* Dots */}
          <div className="mt-8 flex justify-center gap-2">
            {testimonials.map((t, i) => (
              <button
                key={t.id}
                type="button"
                onClick={() => emblaApi?.scrollTo(i)}
                className={`h-2 rounded-full transition-all ${
                  i === testimonialSnap
                    ? "w-8 bg-[#D4A017]"
                    : "w-2 bg-[#D4A017]/40 hover:bg-[#D4A017]/60"
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Left: category tabs, Right: FAQ content */}
      <section
        id="faq"
        className="scroll-mt-20 bg-gradient-to-b from-white to-[#FFF5E6] py-16 sm:py-20 lg:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center sm:mb-16">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D4A017]/30 bg-[#FFF9F0] px-4 py-2 text-sm text-[#7A1E1E]">
              <HelpCircle className="h-4 w-4 text-[#D4A017]" />
              Got questions?
            </div>
            <h2 className="mb-3 text-2xl font-semibold text-[#7A1E1E] sm:mb-4 sm:text-3xl md:text-4xl lg:text-5xl">
              Frequently Asked Questions
            </h2>
            <div className="mx-auto mb-4 h-1 w-32 bg-gradient-to-r from-transparent via-[#D4A017] to-transparent" />
            <p className="mx-auto max-w-2xl text-base text-gray-600 sm:text-lg">
              Find answers to common questions about Jain Boarding Federation
              (JBF) and our services.
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
            {/* Left: Category tabs */}
            <div className="flex-shrink-0 lg:w-56 xl:w-64">
              <div className="flex flex-wrap gap-2 lg:flex-col lg:flex-nowrap">
                {loadingFaqs ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="h-10 w-full animate-pulse rounded-lg bg-[#FFF9F0]"
                      />
                    ))}
                  </div>
                ) : (
                  faqCategories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        setSelectedFaqCategory(cat);
                        setOpenFaqId(null);
                      }}
                      className={`w-full rounded-lg border px-4 py-3 text-left text-sm font-medium transition-all ${
                        selectedFaqCategory === cat
                          ? "border-[#D4A017] bg-[#FFF9F0] text-[#7A1E1E] shadow-sm"
                          : "border-[#D4A017]/30 bg-white text-gray-600 hover:border-[#D4A017]/50 hover:bg-[#FFF9F0]/50"
                      }`}
                    >
                      {cat}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Right: FAQ list for selected category */}
            <div className="min-w-0 flex-1 space-y-4">
              {loadingFaqs ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="h-16 w-full animate-pulse rounded-xl border border-[#D4A017]/10 bg-white"
                    />
                  ))}
                </div>
              ) : filteredFaqItems.length === 0 ? (
                <p className="rounded-xl border border-[#D4A017]/20 bg-white px-4 py-8 text-center text-gray-600">
                  No FAQs in this category.
                </p>
              ) : (
                filteredFaqItems.map(item => (
                  <div
                    key={item.id}
                    className={`overflow-hidden rounded-xl border transition-all ${
                      openFaqId === item.id
                        ? "border-[#D4A017] bg-white shadow-md"
                        : "border-[#D4A017]/20 bg-white hover:border-[#D4A017]/40"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFaqId(prev =>
                          prev === item.id ? null : item.id
                        )
                      }
                      className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left sm:px-5 sm:py-5"
                    >
                      <span className="text-base font-medium text-[#7A1E1E] sm:text-lg">
                        {item.question}
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 flex-shrink-0 text-[#D4A017] transition-transform ${
                          openFaqId === item.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openFaqId === item.id && (
                      <div
                        className="prose prose-slate max-w-none border-t border-[#D4A017]/20 px-4 py-4 text-sm text-gray-700 sm:px-5 sm:py-5 sm:text-base"
                        dangerouslySetInnerHTML={{ __html: item.answer }}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Hostel Registration Information Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="relative max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#7A1E1E] px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white sm:text-2xl">
                    Hostel Registration - Important Information
                  </h2>
                  <p className="mt-1 text-xs text-gray-200 sm:text-sm">
                    Please review the following requirements before proceeding.
                  </p>
                </div>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="rounded-lg p-2 text-white transition-colors hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {/* Documents & Information Required */}
              <div className="mb-6 sm:mb-8">
                <div className="mb-4 flex items-center gap-2 sm:gap-3">
                  <FileText className="h-5 w-5 flex-shrink-0 text-[#7A1E1E] sm:h-6 sm:w-6" />
                  <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                    Documents & Information Required
                  </h3>
                </div>
                <p className="mb-4 text-sm text-gray-700 sm:mb-6 sm:text-base">
                  Please keep the following documents and information ready
                  before starting the registration process:
                </p>

                <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                  {[
                    {
                      icon: <FileText className="h-6 w-6" />,
                      title: "Trust Registration Certificate",
                      description:
                        "Valid registration certificate of the trust/society.",
                    },
                    {
                      icon: <Users className="h-6 w-6" />,
                      title: "Current Occupancy Details",
                      description:
                        "Number of students, capacity, and facilities available.",
                    },
                    {
                      icon: <MapPin className="h-6 w-6" />,
                      title: "Location Information",
                      description: "Complete address with landmark details.",
                    },
                    {
                      icon: <Building2 className="h-6 w-6" />,
                      title: "Property Documents",
                      description:
                        "Ownership or lease documents of hostel premises.",
                    },
                    {
                      icon: <TrendingUp className="h-6 w-6" />,
                      title: "Financial Records",
                      description:
                        "Last 2 years financial statements (if applicable).",
                    },
                    {
                      icon: <Phone className="h-6 w-6" />,
                      title: "Contact Person Details",
                      description:
                        "Name, designation, phone, and email of authorized person.",
                    },
                  ].map(item => (
                    <div
                      key={item.title}
                      className="rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:p-4"
                    >
                      <div className="mb-2 flex items-start gap-2 sm:gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] text-white sm:h-10 sm:w-10">
                          {item.icon}
                        </div>
                        <h4 className="text-sm font-semibold text-[#7A1E1E] sm:text-base">
                          {item.title}
                        </h4>
                      </div>
                      <p className="text-xs text-gray-700 sm:text-sm">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Guidelines */}
              <div className="mb-6 sm:mb-8">
                <div className="mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:h-8 sm:w-8">
                    <AlertCircle className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                    Important Guidelines
                  </h3>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    "Ensure all documents are clear and legible",
                    "Information provided should be accurate and up-to-date",
                    "You will receive confirmation within 3-5 business days",
                    "A JBF representative may contact you for verification",
                    "Incomplete applications will not be processed",
                  ].map(guideline => (
                    <li
                      key={guideline}
                      className="flex items-start gap-2 sm:gap-3"
                    >
                      <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:mt-1 sm:h-5 sm:w-5">
                        <CheckCircle className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
                      </div>
                      <span className="text-xs text-gray-700 sm:text-sm">
                        {guideline}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Need Help? */}
              <div className="mb-4 rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:mb-6 sm:p-4">
                <div className="mb-2 flex items-center gap-2 sm:gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-[#7A1E1E] sm:h-5 sm:w-5" />
                  <h3 className="text-sm font-semibold text-[#7A1E1E] sm:text-base">
                    Need Help?
                  </h3>
                </div>
                <p className="text-xs text-gray-700 sm:text-sm">
                  If you have any questions about the registration process,
                  please contact us at{" "}
                  <a
                    href="mailto:registration@jbfindia.org"
                    className="break-all text-[#7A1E1E] underline hover:text-[#5A1515]"
                  >
                    registration@jbfindia.org
                  </a>{" "}
                  or call +91 XXXXX XXXXX.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto sm:px-6 sm:py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedToForm}
                  className="w-full rounded-lg bg-[#7A1E1E] px-4 py-2.5 text-sm text-white transition-colors hover:bg-[#5A1515] sm:w-auto sm:px-6 sm:py-3"
                >
                  I&apos;m Ready - Proceed to Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hostel Registration Form Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="relative max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-[#FFF9F0] shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white px-4 py-3 shadow-sm sm:px-6 sm:py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] sm:h-10 sm:w-10">
                    <Building2 className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#7A1E1E] sm:text-2xl">
                      Hostel Registration
                    </h2>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      Submit an expression of interest
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRegistrationForm(false)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="p-4 sm:p-6">
              <div className="space-y-4 sm:space-y-6">
                {/* Hostel Name */}
                <div>
                  <label
                    htmlFor="hostelName"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Hostel Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="hostelName"
                    name="hostelName"
                    value={hostelFormData.hostelName}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter hostel name"
                  />
                </div>

                {/* Trust Name */}
                <div>
                  <label
                    htmlFor="trustName"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Trust/Society Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="trustName"
                    name="trustName"
                    value={hostelFormData.trustName}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter trust/society name"
                  />
                </div>

                {/* Address */}
                <div>
                  <label
                    htmlFor="address"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={hostelFormData.address}
                    onChange={handleHostelFormChange}
                    required
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter complete address"
                  />
                </div>

                {/* City, State, Pincode */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label
                      htmlFor="city"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={hostelFormData.city}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="state"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={hostelFormData.state}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="pincode"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Pincode <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={hostelFormData.pincode}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Pincode"
                    />
                  </div>
                </div>

                {/* Contact Person */}
                <div>
                  <label
                    htmlFor="contactPerson"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Contact Person Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={hostelFormData.contactPerson}
                    onChange={handleHostelFormChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                    placeholder="Enter contact person name"
                  />
                </div>

                {/* Phone and Email */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={hostelFormData.phone}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={hostelFormData.email}
                      onChange={handleHostelFormChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>

                {/* Capacity and Current Occupancy */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="capacity"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Total Capacity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={hostelFormData.capacity}
                      onChange={handleHostelFormChange}
                      required
                      min="1"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter total capacity"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="currentOccupancy"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Current Occupancy <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="currentOccupancy"
                      name="currentOccupancy"
                      value={hostelFormData.currentOccupancy}
                      onChange={handleHostelFormChange}
                      required
                      min="0"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                      placeholder="Enter current occupancy"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-2 sm:pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#5A1515] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Registration
                        <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <DonorInquiryModal
        open={showDonorModal}
        onClose={() => setShowDonorModal(false)}
      />
    </div>
  );
}
